/**
 * Form configurations for the Conference Room Designer.
 * This module defines form elements that are used in menus and the walkthrough guide.
 * These were previously part of modals but have been extracted for reuse.
 * @module conferenceRoomDesignerForms
 */
import constants from "toolConstants/conferenceroomDesigner/constants";
import { side } from "utilities/mathematic";
import { keys, round, indexOf, every } from "lodash";

const { BAR, CEILING_MOUNTED, PENDANT } = constants.equipment.micCategories;
// handles both migrated keys (e.g. 'vbc_2500a') and legacy 'bar' key
const isBarMic = (model) => model === 'bar' || BAR.has(model);

/**
 * Form configurations for Conference Room Designer features.
 * These forms are used in menus and the walkthrough guide.
 */
const forms = {
  area: {
    body: [
      {
        element: 'textInput',
        name: 'name',
        label: 'Room Name',
        dispatcher: 'setName'
      },
      {
        element: 'dimensionInput',
        label: 'Room Dimensions',
        ids: ['length', 'width'], // First input controls length (horizontal/X), second controls width (vertical/Y)
        dispatcher: 'setDimensions',
        errorCode: 1000,
      },
      {
        element: 'dimensionInput',
        label: 'Ceiling Height',
        ids: ['min'],
        dispatcher: 'setCeilingHeight',
        errorCode: 1010,
      },
      {
        element: 'selector',
        label: 'Ceiling tile dimensions',
        name: 'tile',
        items: keys(constants.ceilingTiles),
        dispatcher: 'setCeilingTiles'
      },
      {
        element: 'unitSwitch'
      }
    ]
  },

  table: {
    body: [
      {
        element: 'toggleButtons',
        id: 'tableSelect',
        errorCode: 1050,
        extend: true,
        label: 'Table shape',
        items: keys(constants.tables),
        deselect: true,
        name: 'shape',
        theme: 'furnitureGroup',
        activeState: 'furniture_active',
        inactiveState: "furniture_inactive",
        dispatcher: 'setTable'
      },
      {
        condition: { parameter: ['table'], check: ([table]) => table.shape && table.shape !== 'round' },
        element: 'dimensionInput',
        label: 'Dimensions',
        ids: ['size.length', 'size.width'],
        dispatcher: 'setTable',
        errorCode: 1050
      },
      {
        condition: { parameter: ['table'], check: ([table]) => table.shape && table.shape === 'round' },
        element: 'dimensionInput',
        label: 'Diameter',
        ids: ['size.length'],
        dispatcher: 'setTable',
        errorCode: 1050
      },
      {
        element: 'unitSwitch'
      }
    ]
  },

  devices: {
    body: [
      {
        element: 'toggleButtons',
        extend: true,
        label: 'Mic Style',
        name: 'model',
        items: {
          withTable: keys(constants.equipment.microphones).filter(mic => mic !== 'tabletop_mic'),
          withoutTable: keys(constants.equipment.microphones),
          withTableHigh: keys(constants.equipment.microphones).filter(mic => !['ceiling_mic', 'tabletop_mic'].includes(mic)),
          withoutTableHigh: keys(constants.equipment.microphones).filter(mic => mic !== 'ceiling_mic')
        },
        keyCondition: {
          parameter: ['table', 'ceilingHeight', 'acoustics'], check: ([table, ceiling, acc]) => {
            const acoustics = constants.acoustics;
            const talkerHeight = constants.area.talkerHeight;
            const acousticIndex = indexOf(acoustics, acc);
            // Use cached calculation to avoid expensive side() computation
            const max = constants.getCachedCeilingMicCalculation ? 
              constants.getCachedCeilingMicCalculation(acousticIndex, talkerHeight) :
              round(side(constants.equipment.microphones.ceiling_mic.micData[acousticIndex], 1) + talkerHeight, 4);
            return `with${table.shape ? 'out' : ''}Table${ceiling.min > max ? 'High' : ''}`
          }
        },
        labels: true,
        theme: 'deviceGroup',
        activeState: 'device_selected',
        dispatcher: 'setMicrophones',
        infoCode: 1100
      },
      {
        condition: { parameter: ['microphones'], check: ([microphones]) => microphones?.model && PENDANT.has(microphones.model) },
        className: 'ms-2',
        element: 'dimensionInput',
        label: 'Microphone Height',
        ids: ['height'],
        dispatcher: 'setMicrophones',
        infoCode: 1110
      },
      {
        condition: { parameter: ['table', 'microphones'], check: ([table, microphones]) => table?.shape && microphones?.model && (CEILING_MOUNTED.has(microphones.model) || PENDANT.has(microphones.model)) },
        element: 'toggleSelect',
        extend: true,
        label: 'Mic Coverage',
        // todo: get the toggles from constants 
        items: ['full_room', 'focused'],
        labels: true,
        noSelect: 'focused',
        name: 'coverage',
        theme: 'buttonGroupBig',
        activeState: 'coverage_active',
        inactiveState: "coverage_inactive",
        dispatcher: 'setMicrophones',
      },
      {
        element: 'toggleButtons',
        label: 'Room Acoustics',
        items: constants.acoustics, 
        labels: true,
        theme: 'acousticGroup',
        inactiveState: 'acoustic_inactive',
        dispatcher: 'setAcoustics',
        infoLink: "https://support.biamp.com/Tesira/Parle_microphones/Parl%C3%A9_ceiling_microphone_calculator/Sample_recordings",
        infoText: 'What do these settings mean?',
        extend: true,
      },
      {
        // cleanup
        condition: { parameter: ['microphones'], check: ([microphones]) => microphones?.model && !isBarMic(microphones.model) },
        element: 'toggleSelect',
        extend: true,
        label: 'Speaker Coverage',
        // todo: grab from constants
        items: ['full_room', 'focused'],
        labels: true,
        noSelect: 'focused',
        name: 'coverage',
        theme: 'buttonGroupBig',
        activeState: 'coverage_active',
        inactiveState: "coverage_inactive",
        dispatcher: 'setSpeakers'
      },
      {
        // Show height field for pendant and wall speakers
        condition: {
          parameter: ['speakers', 'microphones'],
          check: ([speakers, microphones]) => {
            const model = speakers?.model;
            const isPendant = model === 'pendantspkr';
            const isWall = model === 'wallspkr';
            const micModel = microphones?.model;
            const notBar = !isBarMic(micModel);
            return (isPendant || isWall) && notBar;
          }
        },
        className: 'ms-2',
        element: 'dimensionInput',
        label: 'Speaker height',
        ids: ['height'],
        dispatcher: 'setSpeakers'
      },
      {
        condition: { parameter: ['microphones'], check: ([microphones]) => microphones?.model && !isBarMic(microphones.model) },
        element: 'slider',
        label: 'Camera',
        dispatcher: 'setCameras',
        name: 'hasCamera',
      },
      {
        element: 'unitSwitch'
      }
    ]
  }
};

export default forms;